import { exportCreditsBoxes } from '~/client/src/shared/components/MapBoxViewer/drawMapCredits'
import { textScaleForZoom } from '~/client/src/shared/components/MapMarkups/markupTextScale'

import { rotatedBox } from './exportLabelAlongPath'
import {
  IBox,
  IExportKeyMember,
  IExportLabelFeature,
  IExportLabelScene,
  IExportObstacle,
  IExportSceneMarker,
  IExportScenePin,
  ExportLabelKind,
  IExportLabelInk,
  IExportLabelStack,
  LABEL_CAP_PX,
} from './exportLabelTypes'
import { exportWatermark } from './exportWatermark'
import { ILabelTier } from './labelLayerStyle'
import { IExportLayout } from './mapExportPaper'
import {
  MARKER_CLEARANCE_PX,
  MARKER_COLLISION_HALF_PX,
  PIN_SPEC,
} from './markerGeometry'

const TILE_SIZE_PX = 512
const MAX_MERCATOR_LATITUDE = 85.051129

export interface IExportCamera {
  center: [number, number]
  zoom: number
  bearing: number
}

export type ExportLabelIconAt =
  | 'pinAbove'
  | 'pinAboveAnchored'
  | 'markerCentre'
  | null

// An input with no text labels nothing: it only reserves its icon, so labels keep off it.
export interface IExportLabelInput {
  id: string
  kind: ExportLabelKind
  lngLat: [number, number]
  text: string
  tier: ILabelTier
  sizePx?: number
  iconAt?: ExportLabelIconAt
  priority: number
  sortKey: number
  pinKey?: string
  pinCount?: number
  // The names a text-less pin stands for, listed under its group in the label key.
  members?: IExportKeyMember[]
  markerKey?: string
  ink?: IExportLabelInk
  path?: Array<[number, number]>
  isClosed?: boolean
  stack?: IExportLabelStack
  stackCount?: number
  // A text markup: its box in layout px at text scale 1, centred on lngLat, turned clockwise by
  // `rotation` degrees in the map plane, and its font size.
  textBox?: {
    width: number
    height: number
    rotation: number
    fontSizePx: number
  }
}

// A label's own size held to print's cap, then the user's percent, never under the floor.
export const exportLabelSizePx = (
  ownPx: number,
  percent: number,
  floorPx: number,
): number => Math.max(floorPx, (Math.min(LABEL_CAP_PX, ownPx) * percent) / 100)

// The size a label sets for itself, else its tier's.
export const labelOwnSizePx = (input: IExportLabelInput): number =>
  input.sizePx ?? input.tier.sizePx

// A label run along a markup is an annotation, not a name: a size down and a weight lighter.
const ALONG_PATH_SIZE_SHARE = 0.75
const ALONG_PATH_WEIGHT = 400

export const alongPathSizePx = (tierSizePx: number, floorPx: number): number =>
  Math.max(floorPx, ALONG_PATH_SIZE_SHARE * tierSizePx)

const mercatorPx = (
  [lng, lat]: [number, number],
  worldSize: number,
): { x: number; y: number } => {
  const clamped = Math.max(
    -MAX_MERCATOR_LATITUDE,
    Math.min(MAX_MERCATOR_LATITUDE, lat),
  )
  const y =
    (180 -
      (180 / Math.PI) *
        Math.log(Math.tan(Math.PI / 4 + (clamped * Math.PI) / 360))) /
    360
  return { x: ((lng + 180) / 360) * worldSize, y: y * worldSize }
}

export function projectToLayout(
  lngLat: [number, number],
  camera: IExportCamera,
  layout: { cssWidth: number; cssHeight: number },
): { x: number; y: number } {
  const worldSize = TILE_SIZE_PX * 2 ** camera.zoom
  const point = mercatorPx(lngLat, worldSize)
  const centre = mercatorPx(camera.center, worldSize)
  const dx = point.x - centre.x
  const dy = point.y - centre.y
  const theta = (camera.bearing * Math.PI) / 180
  return {
    x: dx * Math.cos(theta) + dy * Math.sin(theta) + layout.cssWidth / 2,
    y: -dx * Math.sin(theta) + dy * Math.cos(theta) + layout.cssHeight / 2,
  }
}

export function unprojectFromLayout(
  point: { x: number; y: number },
  camera: IExportCamera,
  layout: { cssWidth: number; cssHeight: number },
): [number, number] {
  const worldSize = TILE_SIZE_PX * 2 ** camera.zoom
  const centre = mercatorPx(camera.center, worldSize)
  const rx = point.x - layout.cssWidth / 2
  const ry = point.y - layout.cssHeight / 2
  const theta = (camera.bearing * Math.PI) / 180
  const x = centre.x + rx * Math.cos(theta) - ry * Math.sin(theta)
  const y = centre.y + rx * Math.sin(theta) + ry * Math.cos(theta)
  const lng = (x / worldSize) * 360 - 180
  const lat =
    (360 / Math.PI) *
      Math.atan(Math.exp(((180 - (y / worldSize) * 360) * Math.PI) / 180)) -
    90
  return [lng, lat]
}

// A pin over a Location icon is lifted clear of the icon, so its lift takes the icon's scale.
function iconBox(
  iconAt: ExportLabelIconAt | undefined,
  anchor: { x: number; y: number },
  scales: { pinIconScale: number; markerIconScale: number },
): IBox | null {
  if (iconAt === 'markerCentre') {
    const half = MARKER_COLLISION_HALF_PX * scales.markerIconScale
    return {
      x: anchor.x - half,
      y: anchor.y - half,
      width: 2 * half,
      height: 2 * half,
    }
  }
  if (iconAt === 'pinAbove' || iconAt === 'pinAboveAnchored') {
    const height = PIN_SPEC.boxPx * scales.pinIconScale
    const width = (height * PIN_SPEC.viewBoxWidth) / PIN_SPEC.viewBoxHeight
    const lift =
      iconAt === 'pinAboveAnchored'
        ? MARKER_CLEARANCE_PX * scales.markerIconScale
        : 0
    return {
      x: anchor.x - width / 2,
      y: anchor.y - lift - height,
      width,
      height,
    }
  }
  return null
}

// A text markup on the page: scaled with the map as the map draws it, times Markup text around its
// centre, and turned with the map; its upright bounds, carrying the turned box itself.
function textMarkupBox(
  box: NonNullable<IExportLabelInput['textBox']>,
  anchor: { x: number; y: number },
  camera: IExportCamera,
  sizeScale: number,
): IExportObstacle {
  const scale = textScaleForZoom(camera.zoom) * sizeScale
  const shape = {
    centre: anchor,
    width: box.width * scale,
    height: box.height * scale,
    angleRad: ((box.rotation - camera.bearing) * Math.PI) / 180,
  }
  return {
    ...rotatedBox(anchor, shape.width, shape.height, shape.angleRad),
    shape,
  }
}

export function buildExportLabelScene(
  inputs: IExportLabelInput[],
  camera: IExportCamera,
  layout: IExportLayout,
  opts: {
    namePercent: Record<ExportLabelKind, number>
    nameFloorPx: number
    pinIconScale: number
    markerIconScale: number
    textZoomScale: number
    markupTextScale: number
    brandMarkScale: number
  },
): IExportLabelScene {
  const features: IExportLabelFeature[] = []
  const obstacles: IExportObstacle[] = [
    exportWatermark(opts.brandMarkScale).plate,
    ...exportCreditsBoxes(
      layout.cssWidth,
      layout.cssHeight,
      opts.brandMarkScale,
    ),
  ]
  const pins: IExportScenePin[] = []
  const markers: IExportSceneMarker[] = []
  inputs.forEach(input => {
    const anchor = projectToLayout(input.lngLat, camera, layout)
    const icon = input.textBox
      ? textMarkupBox(input.textBox, anchor, camera, opts.markupTextScale)
      : iconBox(input.iconAt, anchor, opts)
    if (!input.text) {
      if (icon) obstacles.push(icon)
      if (icon && input.kind === 'workflowPin' && input.pinKey) {
        pins.push({
          key: input.pinKey,
          icon,
          count: input.pinCount ?? 1,
          members: input.members ?? [],
        })
      }
      if (icon && input.kind === 'location' && input.markerKey) {
        markers.push({ key: input.markerKey, icon })
      }
      return
    }
    const { tier } = input
    const tierSizePx = exportLabelSizePx(
      labelOwnSizePx(input) * opts.textZoomScale,
      opts.namePercent[input.kind],
      opts.nameFloorPx,
    )
    const sizePx = input.path
      ? alongPathSizePx(tierSizePx, opts.nameFloorPx)
      : tierSizePx
    features.push({
      id: input.id,
      kind: input.kind,
      anchor,
      icon,
      text: input.text,
      sizePx,
      lineHeightPx: (sizePx * tier.lineHeightPx) / tier.sizePx,
      weight: input.path ? ALONG_PATH_WEIGHT : tier.weightCss,
      letterSpacingEm: tier.letterSpacingEm,
      priority: input.priority,
      sortKey: input.sortKey,
      pinKey: input.pinKey,
      pinCount: input.pinCount,
      markerKey: icon ? input.markerKey : undefined,
      ink: input.ink,
      path: input.path?.map(point => projectToLayout(point, camera, layout)),
      isClosed: input.isClosed,
      stack: input.stack,
      stackCount: input.stackCount,
    })
  })
  return {
    width: layout.cssWidth,
    height: layout.cssHeight,
    features,
    obstacles,
    pins,
    markers,
  }
}
